import {useContext, useState} from 'react'

import {toQueryString} from '@s-ui/js/lib/string'
import Context from '@s-ui/react-context'
import {useRouter} from '@s-ui/react-router'

import {STEPS, VEHICLE_FORM_ORIGINS} from './config.js'
import {useLandingInstantOfferTracking} from './useLandingInstantOfferTracking.js'

const MAX_VEHICLE_OPTIONS = 5

const useLandingInstantOffer = () => {
  const {domain} = useContext(Context)
  const {push} = useRouter()

  const {C2B_ORIGINS} = domain.get('config').get('c2b')

  const {
    trackSubmitPlateFormError,
    trackSubmitPlateFormSuccess,
    trackSubmitUserFormSuccess,
    trackSubmitVehicleFormSuccess
  } = useLandingInstantOfferTracking()

  const [currentStep, setCurrentStep] = useState(STEPS.LANDING)
  const [makesList, setMakesList] = useState([])
  const [sellerVehicle, setSellerVehicle] = useState({})
  const [vehicleList, setVehicleList] = useState([])
  const [vehicleFormOrigin, setVehicleFormOrigin] = useState('')

  const setStep = step => {
    window.scrollTo(0, 0)
    setCurrentStep(step)
  }

  const getMakesList = async () => {
    const [getMakesError, getMakesResponse] = await domain.get('get_specs_form_use_case').execute()

    if (!getMakesError) setMakesList(getMakesResponse?.datalist)
  }

  const setConfirmedVehicle = ({vehicle}) => {
    const {bodyTypeId, doors, fuelTypeId, makeId, make, modelId, model, versionId, version, year, transmissionTypeId} =
      vehicle

    const _sellerVehicle = {
      bodyTypeDoors: `${bodyTypeId.toString()}-${doors.toString()}`,
      fuelId: fuelTypeId,
      makeId,
      makeName: make,
      modelId,
      modelName: model,
      transmissionTypeId: transmissionTypeId.toString(),
      versionId,
      versionName: version,
      year,
      ...sellerVehicle
    }

    setSellerVehicle(_sellerVehicle)
  }

  const handleClickConfirmVehicle = ({vehicle}) => {
    setConfirmedVehicle({vehicle})
    setStep(STEPS.USER_FORM)
  }

  const handleClickEditVehicleData = async ({vehicle}) => {
    await getMakesList()
    setConfirmedVehicle({vehicle})
    setVehicleFormOrigin(VEHICLE_FORM_ORIGINS.EDIT_DATA)
    setStep(STEPS.VEHICLE_FORM)
  }

  const handleClickNotSellerVehicle = async () => {
    await getMakesList()
    const origin =
      vehicleList.length > 1 ? VEHICLE_FORM_ORIGINS.NO_VEHICLE_IN_RESULTS : VEHICLE_FORM_ORIGINS.WRONG_RESULT
    setVehicleFormOrigin(origin)
    setStep(STEPS.VEHICLE_FORM)
  }

  const handleBackNavigation = () => {
    const {km, plate, vehicleStatus} = sellerVehicle
    setSellerVehicle({km, plate, vehicleStatus})
    setVehicleFormOrigin('')
    setStep(STEPS.LANDING)
  }

  const handlePlateFormGetVehicles = async ({fields, getVehiclesResponse}) => {
    setSellerVehicle(fields)

    const [error, response] = getVehiclesResponse

    if (error) {
      trackSubmitPlateFormError()
      await getMakesList()
      setVehicleFormOrigin(VEHICLE_FORM_ORIGINS.PLATE_ERROR)
      setStep(STEPS.VEHICLE_FORM)
      return
    }

    trackSubmitPlateFormSuccess()
    setVehicleList(response.items.slice(0, MAX_VEHICLE_OPTIONS))
    setStep(STEPS.VEHICLE_SELECT)
  }

  const handleVehicleFormSubmit = ({vehicle}) => {
    trackSubmitVehicleFormSuccess()
    setSellerVehicle(vehicle)
    setStep(STEPS.USER_FORM)
  }

  const handleUserDataFormSubmit = ({userData}) => {
    trackSubmitUserFormSuccess()
    domain
      .get('get_instant_offer_seller_use_case')
      .execute({
        ...userData,
        ...sellerVehicle
      })
      .then(([error, response]) => {
        const {INSTANT_OFFER_SELL_TO_DEALER_PATTERN} = domain.get('config').get('page')

        if (error) {
          const queryParams = toQueryString({
            origin: C2B_ORIGINS.STANDALONE_EMPTY_CASE,
            ...sellerVehicle
          })
          push(`/vende-a-concesionario/?${queryParams}`)
          return
        }
        push(
          `${INSTANT_OFFER_SELL_TO_DEALER_PATTERN}/?ioId=${response.instantOfferId}&origin=${C2B_ORIGINS.INSTANT_OFFER_LANDING}`
        )
      })
  }

  return {
    config: {
      STEPS
    },
    handlers: {
      handleClickConfirmVehicle,
      handleClickEditVehicleData,
      handleClickNotSellerVehicle,
      handleBackNavigation,
      handlePlateFormGetVehicles,
      handleUserDataFormSubmit,
      handleVehicleFormSubmit
    },
    state: {
      currentStep,
      makesList,
      sellerVehicle,
      vehicleList,
      vehicleFormOrigin
    }
  }
}

export {useLandingInstantOffer}
